// Canvas pool for reusing canvas elements
export class CanvasPool {
  constructor({
    maxPoolSize = 200,
    enableSizeGrouping = true,
    maxSizeVariation = 0.2,
    cleanupThreshold = 0.8,
    autoOptimizationInterval = 5000,
  } = {}) {
    this.pool = [];
    this.sizeGroups = new Map(); // 按尺寸分组的pool
    this.maxPoolSize = maxPoolSize;
    this.enableSizeGrouping = enableSizeGrouping;
    this.maxSizeVariation = maxSizeVariation; // 最大尺寸变化比例
    this.cleanupThreshold = cleanupThreshold; // 清理阈值
    this.autoOptimizationInterval = autoOptimizationInterval;

    this.stats = {
      hits: 0,
      misses: 0,
      created: 0,
      released: 0,
      cleaned: 0,
      sizeGroupHits: 0,
    };

    // 自动优化相关配置
    this.optimizationInterval = null;
    this.optimizationCounter = 0;
    this.optimizationThreshold = 100; // 每100次操作后尝试优化
    this.lastOptimizationTime = Date.now();

    // 启动自动清理
    this.startAutoCleanup();
  }

  acquire(width, height) {
    this.optimizationCounter++;
    const targetSize = width * height;

    // 尝试从尺寸分组中获取
    if (this.enableSizeGrouping) {
      const groupedItem = this.acquireFromSizeGroup(width, height, targetSize);
      if (groupedItem) {
        this.stats.hits++;
        this.stats.sizeGroupHits++;
        this.checkAutoOptimize();
        return groupedItem;
      }
    }

    // 传统方式查找
    for (let i = 0; i < this.pool.length; i++) {
      const item = this.pool[i];
      if (this.isCanvasSuitable(item.canvas, width, height)) {
        this.pool.splice(i, 1);
        this.prepareCanvas(item, width, height);
        this.stats.hits++;
        this.checkAutoOptimize();
        return item;
      }
    }

    // 创建新canvas
    const newItem = this.createNewCanvas(width, height);
    this.stats.misses++;
    this.stats.created++;
    this.checkAutoOptimize();
    return newItem;
  }

  acquireFromSizeGroup(width, height, targetSize) {
    const tolerance = targetSize * this.maxSizeVariation;

    for (const [groupSize, items] of this.sizeGroups) {
      if (Math.abs(groupSize - targetSize) <= tolerance && items.length > 0) {
        const item = items.pop();
        if (items.length === 0) {
          this.sizeGroups.delete(groupSize);
        }
        this.prepareCanvas(item, width, height);
        return item;
      }
    }
    return null;
  }

  isCanvasSuitable(canvas, width, height) {
    const currentSize = canvas.width * canvas.height;
    const targetSize = width * height;
    const sizeRatio = currentSize / targetSize;

    // Canvas应该足够大，但不要过度大（避免内存浪费）
    return canvas.width >= width && canvas.height >= height && sizeRatio <= 2; // 不超过目标尺寸的2倍
  }

  prepareCanvas(item, width, height) {
    // 强制重置canvas（设置不同的宽度再设置回来确保完全重置所有状态，包括clip路径）
    const needsReset = item.canvas.width === width && item.canvas.height === height;
    if (needsReset) {
      item.canvas.width = width + 1;
    }
    item.canvas.width = width;
    item.canvas.height = height;

    // 重置context状态
    const ctx = item.context;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = '#000000';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1;
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'miter';
    ctx.miterLimit = 10;
    // 清除任何残留的路径
    ctx.beginPath();

    return item;
  }

  createNewCanvas(width, height) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', {
      alpha: true,
      willReadFrequently: false, // 优化性能
    });
    canvas.width = width;
    canvas.height = height;

    return { canvas, context, createdAt: Date.now() };
  }

  release(item) {
    if (!item || !item.canvas || !item.context) return;

    const poolSize = this.pool.length + this.getTotalSizeGroupItems();
    if (poolSize >= this.maxPoolSize) {
      this.stats.released++;
      return; // 池已满，直接丢弃
    }

    // 清理canvas
    const canvas = item.canvas;
    const context = item.context;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    // 清除任何残留的路径和状态
    context.beginPath();
    context.globalAlpha = 1;
    context.globalCompositeOperation = 'source-over';

    // 添加释放时间戳
    item.releasedAt = Date.now();

    if (this.enableSizeGrouping) {
      this.releaseToSizeGroup(item);
    } else {
      this.pool.push(item);
    }

    this.stats.released++;
  }

  releaseToSizeGroup(item) {
    const size = item.canvas.width * item.canvas.height;

    if (!this.sizeGroups.has(size)) {
      this.sizeGroups.set(size, []);
    }

    const group = this.sizeGroups.get(size);
    group.push(item);

    // 限制每个尺寸组的大小
    const maxGroupSize = Math.max(10, Math.floor(this.maxPoolSize / 10));
    if (group.length > maxGroupSize) {
      group.shift(); // 移除最旧的
    }
  }

  getTotalSizeGroupItems() {
    let total = 0;
    for (const items of this.sizeGroups.values()) {
      total += items.length;
    }
    return total;
  }

  clear() {
    this.pool = [];
    this.sizeGroups.clear();
    this.stats = {
      hits: 0,
      misses: 0,
      created: 0,
      released: 0,
      cleaned: 0,
      sizeGroupHits: 0,
    };
    this.optimizationCounter = 0;
    this.stopAutoOptimization();
  }

  // 清理过期的canvas
  cleanup() {
    const now = Date.now();
    const maxAge = 30000; // 30秒后清理未使用的canvas
    let cleaned = 0;

    // 清理主pool
    this.pool = this.pool.filter((item) => {
      if (item.releasedAt && now - item.releasedAt > maxAge) {
        cleaned++;
        return false;
      }
      return true;
    });

    // 清理尺寸分组
    for (const [size, items] of this.sizeGroups) {
      const filteredItems = items.filter((item) => {
        if (item.releasedAt && now - item.releasedAt > maxAge) {
          cleaned++;
          return false;
        }
        return true;
      });

      if (filteredItems.length === 0) {
        this.sizeGroups.delete(size);
      } else {
        this.sizeGroups.set(size, filteredItems);
      }
    }

    this.stats.cleaned += cleaned;
    return cleaned;
  }

  // 启动自动清理
  startAutoCleanup() {
    setInterval(() => {
      this.cleanup();
    }, 10000); // 每10秒清理一次
  }

  // Get pool statistics for debugging/optimization
  getStats() {
    const totalPooled = this.pool.length + this.getTotalSizeGroupItems();
    return {
      ...this.stats,
      poolSize: this.pool.length,
      sizeGroups: this.sizeGroups.size,
      totalPooled,
      hitRate: this.stats.hits / (this.stats.hits + this.stats.misses) || 0,
      sizeGroupHitRate: this.stats.sizeGroupHits / this.stats.hits || 0,
      efficiency: this.stats.hits / this.stats.created || 0,
    };
  }

  // 检查是否需要自动优化
  checkAutoOptimize() {
    if (this.optimizationCounter >= this.optimizationThreshold) {
      this.optimizePoolSize();
      this.optimizationCounter = 0;
    }
  }

  // Dynamically adjust pool size based on usage patterns
  optimizePoolSize() {
    const stats = this.getStats();
    const timeSinceLastOptimization = Date.now() - this.lastOptimizationTime;

    // 至少间隔1秒才进行优化
    if (timeSinceLastOptimization < 1000) return;

    // 如果命中率低且创建了很多canvas，增加池大小
    if (stats.hitRate < 0.7 && stats.created > 50) {
      this.maxPoolSize = Math.min(this.maxPoolSize * 1.1, 500);
    }

    // 如果命中率很高，可以适当减少池大小
    if (stats.hitRate > 0.95 && stats.totalPooled > 100) {
      this.maxPoolSize = Math.max(this.maxPoolSize * 0.95, 50);
    }

    // 如果池使用率过高，触发清理
    if (stats.totalPooled > this.maxPoolSize * this.cleanupThreshold) {
      this.cleanup();
    }

    this.lastOptimizationTime = Date.now();
  }

  // 启动自动优化（定时器方式）
  startAutoOptimization(interval = null) {
    this.stopAutoOptimization();
    const optimizationInterval = interval || this.autoOptimizationInterval;
    this.optimizationInterval = setInterval(() => {
      this.optimizePoolSize();
    }, optimizationInterval);
  }

  // 停止自动优化
  stopAutoOptimization() {
    if (this.optimizationInterval) {
      clearInterval(this.optimizationInterval);
      this.optimizationInterval = null;
    }
  }

  // 获取池的详细状态
  getDetailedStatus() {
    const stats = this.getStats();
    const sizeGroupSizes = Array.from(this.sizeGroups.entries())
      .map(([size, items]) => ({ size, count: items.length }))
      .sort((a, b) => b.count - a.count);

    return {
      ...stats,
      maxPoolSize: this.maxPoolSize,
      enableSizeGrouping: this.enableSizeGrouping,
      sizeGroupDetails: sizeGroupSizes,
      memoryEstimate: this.estimateMemoryUsage(),
    };
  }

  // 估算内存使用量
  estimateMemoryUsage() {
    let totalPixels = 0;

    // 计算主pool
    for (const item of this.pool) {
      totalPixels += item.canvas.width * item.canvas.height;
    }

    // 计算尺寸分组
    for (const items of this.sizeGroups.values()) {
      for (const item of items) {
        totalPixels += item.canvas.width * item.canvas.height;
      }
    }

    // 每个像素大约4字节(RGBA)
    return totalPixels * 4;
  }
}
